import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { HiOutlineDocumentText, HiExternalLink } from 'react-icons/hi';
import { BillingCycle, Invoice } from './invoiceTypes';
import { getBillingCycleCost, getBillingCycleVatCost } from './invoiceHelper';

export function InvoicePaymentCard({
  billingCycle,
  invoice,
  isSelected,
  onToggleSelected,
  isPaid,
}: {
  billingCycle: BillingCycle;
  invoice: Invoice;
  isSelected?: boolean;
  isPaid?: boolean;
  onToggleSelected: (billingCycle: BillingCycle, isSelecting: boolean) => void;
}) {
  const navigate = useNavigate();
  const subTotalAmount = getBillingCycleCost(billingCycle);
  const subTotalVatAmount = getBillingCycleVatCost(billingCycle);

  const now = moment();
  const timeRemaining = invoice?.dueDate
    ? moment(invoice.dueDate).diff(now, 'days')
    : null;

  const isOverdue = timeRemaining ? timeRemaining < 0 : false;

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-2xl'>
      <div className='p-5'>
        <div className='flex items-start justify-between gap-4'>
          <div className='flex items-start gap-4 flex-1'>
            <div className='flex-shrink-0'>
              <div className='w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center'>
                <HiOutlineDocumentText className='w-5 h-5 text-blue-600' />
              </div>
            </div>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2'>
                <button
                  type='button'
                  onClick={() => navigate(`/invoice/${billingCycle.id}`)}
                  className='text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline inline-flex items-center gap-1'
                >
                  {invoice.invoiceNumber}
                  <HiExternalLink className='w-3.5 h-3.5' />
                </button>
              </div>
              <p className='text-sm text-gray-600 mt-0.5'>
                {billingCycle.lspBusinessName}
              </p>
              <p className='text-xs text-gray-500 mt-1 line-clamp-1'>
                {invoice.lines.map((line) => line.description).join(', ')}
              </p>
            </div>
          </div>

          <div className='flex flex-col items-end gap-2'>
            <span className='text-lg font-bold text-gray-900'>
              {invoice.lines[0]?.currencySymbol || '£'}
              {(subTotalAmount + subTotalVatAmount).toFixed(2)}
            </span>
            {!isPaid && timeRemaining !== null && (
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  isOverdue
                    ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20'
                    : 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
                }`}
              >
                {isOverdue
                  ? `${Math.abs(timeRemaining)} days overdue`
                  : `${timeRemaining} days left`}
              </span>
            )}
            {isPaid && (
              <span className='inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20'>
                Paid
              </span>
            )}
          </div>
        </div>
      </div>

      {!isPaid && (
        <div className='border-t border-gray-100 bg-gray-50 px-5 py-3'>
          <label className='flex items-center gap-3 cursor-pointer'>
            <input
              type='checkbox'
              checked={isSelected}
              onChange={() => onToggleSelected(billingCycle, !isSelected)}
              className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            <span className='text-sm text-gray-600'>Select for payment</span>
          </label>
        </div>
      )}
    </div>
  );
}
